import { useTranslation } from 'react-i18next';
import { Box } from '@mui/joy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface LanguageSwitcherProps {
    color?: 'white' | 'dark';
}

export function LanguageSwitcher({ color = 'white' }: LanguageSwitcherProps) {
    const { i18n } = useTranslation();

    const changeLanguage = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const lng = e.target.value;
        if (lng) {
            i18n.changeLanguage(lng);
        }
    };

    const getLanguageLabel = (lang: string, isMobile: boolean = false) => {
        if (isMobile) {
            const labels: Record<string, string> = {
                en: 'EN',
                fr: 'FR',
            };
            return labels[lang] || lang.toUpperCase();
        } else {
            const labels: Record<string, string> = {
                en: 'English - EN',
                fr: 'Français - FR',
            };
            return labels[lang] || lang.toUpperCase();
        }
    };

    const textColor = color === 'white' ? 'white' : '#212B36';
    // Use resolvedLanguage to get the actual language being used (handles locale codes like 'en-US' -> 'en')
    const currentLang = i18n.resolvedLanguage || i18n.language || 'en';
    const normalizedLang = currentLang.startsWith('en') ? 'en' : currentLang.startsWith('fr') ? 'fr' : 'en';

    return (
        <Box
            sx={{
                position: 'relative',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: { xs: '2px 12px', sm: '4px 16px' },
                gap: { xs: '6px', sm: '8px' },
                width: { xs: '69px', sm: '146px' },
                height: { xs: '32px', sm: '40px' },
                minHeight: { xs: '32px', sm: '40px' },
                border: '1px solid #CDD7E1',
                borderRadius: '6px',
                '& .language-select-mobile': {
                    width: '100%',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 2,
                    padding: { xs: '4px 28px 4px 12px', sm: '4px 16px' }, // Space for arrow on mobile
                    fontSize: '14px',
                    color: { xs: textColor, sm: 'transparent' },
                },
            }}
        >
            <select
                value={normalizedLang}
                onChange={changeLanguage}
                className="language-select-mobile"
                style={{
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '14px',
                    cursor: 'pointer',
                    outline: 'none',
                    fontFamily: 'Inter, sans-serif',
                    width: '100%',
                    height: '100%',
                }}
            >
                <option value="en">{getLanguageLabel('en', true)}</option>
                <option value="fr">{getLanguageLabel('fr', true)}</option>
            </select>
            <Box
                component="span"
                sx={{
                    position: 'absolute',
                    left: '16px',
                    pointerEvents: 'none',
                    color: textColor,
                    fontSize: '14px',
                    display: { xs: 'none', sm: 'inline-block' },
                    opacity: { xs: 0, sm: 1 },
                    zIndex: 1,
                }}
            >
                {getLanguageLabel(normalizedLang, false)}
            </Box>
            <KeyboardArrowDownIcon
                sx={{
                    position: 'absolute',
                    right: { xs: '8px', sm: '12px' },
                    pointerEvents: 'none',
                    color: textColor,
                    fontSize: '16px',
                    flexShrink: 0,
                    zIndex: 1,
                }}
            />
        </Box>
    );
}

